import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  FileText,
  Send,
  CheckCircle,
  Clock,
  Eye,
  DollarSign,
  XCircle,
  AlertTriangle,
  Calendar,
  User,
  Building,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  Copy,
  Download,
  Pencil,
  Receipt,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CounterpartyBadge } from '@/components/trading/sales/counterparty-badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { usePermission } from '@/components/guards/permission-guard';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  SENT: { color: 'bg-blue-100 text-blue-700', icon: Send },
  VIEWED: { color: 'bg-purple-100 text-purple-700', icon: Eye },
  PARTIALLY_PAID: { color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  PAID: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  OVERDUE: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
  WRITTEN_OFF: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
};

const paymentMethods = [
  'CASH',
  'CHECK',
  'BANK_TRANSFER',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'PAYPAL',
  'OTHER',
] as const;

export function TradingInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canEdit = usePermission('INVOICES', 'EDIT', 'TRADING');
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [writeOffModalOpen, setWriteOffModalOpen] = useState(false);

  const [cancelReason, setCancelReason] = useState('');
  const [writeOffReason, setWriteOffReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<typeof paymentMethods[number]>('BANK_TRANSFER');
  const [paymentReference, setPaymentReference] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const {
    data: invoice,
    isLoading,
    refetch,
  } = trpc.tradingInvoice.byId.useQuery(id!, { enabled: !!id });

  const utils = trpc.useUtils();

  const handleDownloadPdf = async () => {
    if (!id) return;
    setPdfDownloading(true);
    try {
      const result = await utils.tradingInvoice.downloadPdf.fetch(id);
      // Decode base64 → Blob → trigger anchor download. Pure client-side
      // hop avoids needing a separate REST endpoint.
      const bin = atob(result.contentBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      showErrorToast(err?.message ?? 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  const sendMutation = trpc.tradingInvoice.send.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.byId.invalidate(id);
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.sent'));
      setSendModalOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const markPaidMutation = trpc.tradingInvoice.markPaid.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.byId.invalidate(id);
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.markedPaid'));
      setPaymentModalOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const cancelMutation = trpc.tradingInvoice.cancel.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.byId.invalidate(id);
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.cancelled'));
      setCancelModalOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const writeOffMutation = trpc.tradingInvoice.writeOff.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.byId.invalidate(id);
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.writtenOff'));
      setWriteOffModalOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const duplicateMutation = trpc.tradingInvoice.duplicate.useMutation({
    onSuccess: (data) => {
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.duplicated'));
      navigate(`/trading/finance/invoices/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('finance.invoices.statusDraft');
      case 'SENT':
        return t('finance.invoices.statusSent');
      case 'VIEWED':
        return t('finance.invoices.statusViewed');
      case 'PARTIALLY_PAID':
        return t('finance.invoices.statusPartiallyPaid');
      case 'PAID':
        return t('finance.invoices.statusPaid');
      case 'OVERDUE':
        return t('finance.invoices.statusOverdue');
      case 'CANCELLED':
        return t('finance.invoices.statusCancelled');
      case 'WRITTEN_OFF':
        return t('finance.invoices.statusWrittenOff');
      default:
        return status;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CASH':
        return t('finance.payments.methodCash');
      case 'CHECK':
        return t('finance.payments.methodCheck');
      case 'BANK_TRANSFER':
        return t('finance.payments.methodBankTransfer');
      case 'CREDIT_CARD':
        return t('finance.payments.methodCreditCard');
      case 'DEBIT_CARD':
        return t('finance.payments.methodDebitCard');
      case 'PAYPAL':
        return t('finance.payments.methodPaypal');
      case 'OTHER':
        return t('finance.payments.methodOther');
      default:
        return method;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleSend = () => {
    if (!id) return;
    sendMutation.mutate({ id });
  };

  const handleMarkPaid = () => {
    if (!id) return;
    markPaidMutation.mutate({
      id,
      paymentMethod,
      reference: paymentReference || undefined,
    });
  };

  const handleCancel = () => {
    if (!id || !cancelReason) return;
    cancelMutation.mutate({ id, reason: cancelReason });
  };

  const handleWriteOff = () => {
    if (!id || !writeOffReason) return;
    writeOffMutation.mutate({ id, reason: writeOffReason });
  };

  const handleDuplicate = () => {
    if (!id) return;
    duplicateMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">{tCommon('notFound')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('finance.invoices.notFoundDesc')}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/trading/finance/invoices')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('finance.invoices.backToList')}
        </Button>
      </div>
    );
  }

  const config = statusConfig[invoice.status] || statusConfig.DRAFT;
  const StatusIcon = config?.icon ?? Clock;

  const canSend = invoice.status === 'DRAFT';
  const canRecordPayment =
    ['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) &&
    invoice.balanceDue > 0;
  const canCancel = invoice.status === 'DRAFT' && invoice.paidAmount === 0;
  const canWriteOff =
    ['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) &&
    invoice.balanceDue > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/trading/finance/invoices')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{invoice.invoiceNumber}</h1>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
              >
                <StatusIcon className="h-3 w-3" />
                {getStatusLabel(invoice.status)}
              </span>
              {invoice.isOverdue && invoice.status !== 'OVERDUE' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                  <AlertTriangle className="h-3 w-3" />
                  {t('finance.invoices.overdue')}
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              {t('finance.invoices.createdOn')} {formatDateTime(invoice.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/trading/finance/invoices/${id}/edit`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {tCommon('edit')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={pdfDownloading}
          >
            <Download className="mr-2 h-4 w-4" />
            {t('finance.invoices.downloadPdf')}
          </Button>
          {canSend && (
            <Button onClick={() => setSendModalOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              {t('finance.invoices.send')}
            </Button>
          )}
          {canRecordPayment && (
            <Button onClick={() => setPaymentModalOpen(true)}>
              <CreditCard className="mr-2 h-4 w-4" />
              {t('finance.invoices.recordPayment')}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Invoice Info & Items */}
        <div className="space-y-6 lg:col-span-2">
          {/* Invoice Details Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {t('finance.invoices.invoiceDetails')}
              </h2>
            </div>
            <div className="p-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.invoiceNumber')}
                  </Label>
                  <p className="font-mono font-medium">{invoice.invoiceNumber}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.status')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {getStatusLabel(invoice.status)}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.currency')}
                  </Label>
                  <p>{invoice.currency}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.issueDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p>{formatDate(invoice.invoiceDate)}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.dueDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className={invoice.isOverdue ? 'text-red-600 font-medium' : ''}>
                      {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                </div>
                {invoice.paymentTerms && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs">
                      {t('finance.invoices.paymentTerms')}
                    </Label>
                    <p>{invoice.paymentTerms}</p>
                  </div>
                )}
              </div>

              {invoice.notes && (
                <div className="mt-4 pt-4 border-t border-border">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.notes')}
                  </Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{invoice.notes}</p>
                </div>
              )}

              {invoice.internalNotes && (
                <div className="mt-4 pt-4 border-t border-border">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.internalNotes')}
                  </Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground">
                    {invoice.internalNotes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Items Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Receipt className="h-5 w-5" />
                  {t('finance.invoices.invoiceItems')}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {invoice.items.length} {t('finance.invoices.items')}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('finance.invoices.description')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('finance.invoices.productCode')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.invoices.quantity')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.invoices.unitPrice')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.invoices.discount')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.invoices.tax')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('finance.invoices.lineTotal')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoice.items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium">{item.description}</p>
                        {item.productName && item.productName !== item.description && (
                          <p className="text-xs text-muted-foreground">{item.productName}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-muted-foreground">
                          {item.productCode || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium">{item.quantity}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(item.unitPrice, invoice.currency)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.discountPercent > 0 ? `${item.discountPercent}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.taxRate > 0 ? `${item.taxRate}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(item.lineTotal, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30">
                  <tr>
                    <td colSpan={6} className="px-4 py-2 text-right text-sm">
                      {t('finance.invoices.subtotal')}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {formatCurrency(invoice.subtotal, invoice.currency)}
                    </td>
                  </tr>
                  {invoice.discountAmount > 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-2 text-right text-sm">
                        {t('finance.invoices.discount')}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-red-600">
                        -{formatCurrency(invoice.discountAmount, invoice.currency)}
                      </td>
                    </tr>
                  )}
                  {invoice.taxAmount > 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-2 text-right text-sm">
                        {t('finance.invoices.taxAmount')}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        {formatCurrency(invoice.taxAmount, invoice.currency)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-border">
                    <td colSpan={6} className="px-4 py-3 text-right font-semibold">
                      {t('finance.invoices.total')}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-lg">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payments Card */}
          {(invoice.payments.length > 0 || invoice.creditApplications.length > 0) && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <CreditCard className="h-5 w-5" />
                  {t('finance.invoices.payments')}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('finance.payments.paymentNumber')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('finance.payments.date')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('finance.payments.method')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        {t('finance.payments.reference')}
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        {t('finance.payments.amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoice.payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">{payment.paymentNumber}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {getPaymentMethodLabel(payment.paymentMethod)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {payment.reference || '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          {formatCurrency(payment.amount, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                    {invoice.creditApplications.map((ca) => (
                      <tr key={ca.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">{ca.creditNoteNumber}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({t('finance.invoices.creditNote')})
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatDate(ca.appliedDate)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {t('finance.invoices.creditApplied')}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">-</td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          {formatCurrency(ca.amount, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30">
                    <tr>
                      <td colSpan={4} className="px-4 py-2 text-right text-sm">
                        {t('finance.invoices.totalPaid')}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-green-600">
                        {formatCurrency(invoice.paidAmount, invoice.currency)}
                      </td>
                    </tr>
                    <tr className="border-t border-border">
                      <td colSpan={4} className="px-4 py-3 text-right font-semibold">
                        {t('finance.invoices.balanceDue')}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold text-lg ${invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}
                      >
                        {formatCurrency(invoice.balanceDue, invoice.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Payment Summary Card (when no payments yet) */}
          {invoice.payments.length === 0 && invoice.creditApplications.length === 0 && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <DollarSign className="h-5 w-5" />
                  {t('finance.invoices.paymentSummary')}
                </h2>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-center py-2">
                  <span>{t('finance.invoices.totalAmount')}</span>
                  <span className="font-bold text-lg">
                    {formatCurrency(invoice.total, invoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span>{t('finance.invoices.totalPaid')}</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(invoice.paidAmount, invoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-t border-border">
                  <span className="font-semibold">{t('finance.invoices.balanceDue')}</span>
                  <span
                    className={`font-bold text-lg ${invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {formatCurrency(invoice.balanceDue, invoice.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Customer & Related Info */}
        <div className="space-y-6">
          {/* Customer Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Building className="h-5 w-5" />
                {t('finance.invoices.customer')}
                <CounterpartyBadge kind={(invoice as any).counterpartyKind} className="ml-2" />
              </h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t('finance.invoices.companyName')}
                </Label>
                <p className="font-medium">{invoice.companyName || '-'}</p>
              </div>
              {invoice.contactName && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.contactName')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <p>{invoice.contactName}</p>
                  </div>
                </div>
              )}
              {invoice.email && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.email')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`mailto:${invoice.email}`}
                      className="text-primary hover:underline"
                    >
                      {invoice.email}
                    </a>
                  </div>
                </div>
              )}
              {invoice.phone && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.phone')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <p>{invoice.phone}</p>
                  </div>
                </div>
              )}
              {invoice.taxId && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('finance.invoices.taxId')}
                  </Label>
                  <p className="font-mono">{invoice.taxId}</p>
                </div>
              )}
              {invoice.tradingCustomerId && (
                <div className="pt-2">
                  <Link
                    to={`/trading/customers/${invoice.tradingCustomerId}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('finance.invoices.viewCustomer')}
                  </Link>
                </div>
              )}
              {(invoice as any).tradingSupplier && (
                <div className="pt-2">
                  <Link
                    to={
                      (invoice as any).tradingSupplier.supplierType === 'DEALER'
                        ? `/trading/cari/dealers/${(invoice as any).tradingSupplier.id}`
                        : (invoice as any).tradingSupplier.supplierType === 'SATICI'
                          ? `/trading/cari/sellers/${(invoice as any).tradingSupplier.id}`
                          : `/trading/cari/suppliers/${(invoice as any).tradingSupplier.id}`
                    }
                    className="text-sm text-primary hover:underline"
                  >
                    {t('cari.viewVendor')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Billing Address Card */}
          {(invoice.billingAddress || invoice.billingCity || invoice.billingCountry) && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <MapPin className="h-5 w-5" />
                  {t('finance.invoices.billingAddress')}
                </h2>
              </div>
              <div className="p-4">
                <address className="not-italic text-sm space-y-1">
                  {invoice.billingAddress && <p>{invoice.billingAddress}</p>}
                  <p>
                    {[invoice.billingCity, invoice.billingState, invoice.billingPostal]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {invoice.billingCountry && <p>{invoice.billingCountry}</p>}
                </address>
              </div>
            </div>
          )}

          {/* Sales Order Card */}
          {invoice.salesOrderId && invoice.salesOrder && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <FileText className="h-5 w-5" />
                  {t('finance.invoices.salesOrder')}
                </h2>
              </div>
              <div className="p-4 space-y-3">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.orders.orderNumber')}
                  </Label>
                  <Link
                    to={`/trading/sales/orders/${invoice.salesOrderId}`}
                    className="block font-mono font-medium text-primary hover:underline"
                  >
                    {invoice.salesOrder.orderNumber}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Created By Card */}
          {invoice.createdBy && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <User className="h-5 w-5" />
                  {t('finance.invoices.createdBy')}
                </h2>
              </div>
              <div className="p-4 space-y-2">
                <p className="font-medium">
                  {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{invoice.createdBy.email}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(invoice.createdAt)}
                </p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">{t('finance.invoices.quickActions')}</h2>
            </div>
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() =>
                  window.open(`/api/trading-invoice/${invoice.id}/pdf`, '_blank')
                }
              >
                <Download className="mr-2 h-4 w-4" />
                {t('finance.invoices.downloadPdf')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleDuplicate}
                disabled={duplicateMutation.isPending}
              >
                {duplicateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                {t('finance.invoices.duplicate')}
              </Button>
              {canWriteOff && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-orange-600 hover:text-orange-700"
                  onClick={() => setWriteOffModalOpen(true)}
                >
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  {t('finance.invoices.writeOff')}
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700"
                  onClick={() => setCancelModalOpen(true)}
                >
                  <Ban className="mr-2 h-4 w-4" />
                  {t('finance.invoices.cancel')}
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/trading/finance/invoices')}
              >
                <FileText className="mr-2 h-4 w-4" />
                {t('finance.invoices.backToList')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Send Invoice Modal */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('finance.invoices.sendInvoice')}</DialogTitle>
            <DialogDescription>{t('finance.invoices.sendInvoiceDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('finance.invoices.sendConfirmMessage', {
                invoiceNumber: invoice.invoiceNumber,
                email: invoice.email,
              })}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModalOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={handleSend} disabled={sendMutation.isPending}>
              {sendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t('finance.invoices.confirmSend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('finance.invoices.recordPayment')}</DialogTitle>
            <DialogDescription>
              {t('finance.invoices.recordPaymentDesc', {
                amount: formatCurrency(invoice.balanceDue, invoice.currency),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">{t('finance.payments.method')}</Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as typeof paymentMethods[number])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method} value={method}>
                      {getPaymentMethodLabel(method)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentReference">{t('finance.payments.reference')}</Label>
              <Textarea
                id="paymentReference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder={t('finance.payments.referencePlaceholder')}
                rows={2}
              />
            </div>
            <div className="p-3 bg-muted/50 border border-border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">{t('finance.invoices.amountToPay')}</span>
                <span className="font-bold text-lg">
                  {formatCurrency(invoice.balanceDue, invoice.currency)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={handleMarkPaid} disabled={markPaidMutation.isPending}>
              {markPaidMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              {t('finance.invoices.confirmPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('finance.invoices.cancelInvoice')}</DialogTitle>
            <DialogDescription>{t('finance.invoices.cancelInvoiceDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancelReason">{t('finance.invoices.cancelReason')}</Label>
              <Textarea
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('finance.invoices.cancelReasonPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.isPending || !cancelReason}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="mr-2 h-4 w-4" />
              )}
              {t('finance.invoices.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write Off Modal */}
      <Dialog open={writeOffModalOpen} onOpenChange={setWriteOffModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('finance.invoices.writeOffInvoice')}</DialogTitle>
            <DialogDescription>{t('finance.invoices.writeOffInvoiceDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-orange-50 border border-orange-200 text-orange-800">
              <p className="text-sm">
                {t('finance.invoices.writeOffWarning', {
                  amount: formatCurrency(invoice.balanceDue, invoice.currency),
                })}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="writeOffReason">{t('finance.invoices.writeOffReason')}</Label>
              <Textarea
                id="writeOffReason"
                value={writeOffReason}
                onChange={(e) => setWriteOffReason(e.target.value)}
                placeholder={t('finance.invoices.writeOffReasonPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteOffModalOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleWriteOff}
              disabled={writeOffMutation.isPending || !writeOffReason}
            >
              {writeOffMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <AlertTriangle className="mr-2 h-4 w-4" />
              )}
              {t('finance.invoices.confirmWriteOff')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TradingInvoiceDetailPage;
